import React, { useState, useEffect, useCallback } from "react";
import {
  Box,
  Typography,
  Card,
  CardContent,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Chip,
  Button,
  TextField,
  MenuItem,
  Grid,
  CircularProgress,
  Paper,
  Divider,
  Tooltip,
  IconButton,
} from "@mui/material";
import {
  ExpandMore as ExpandMoreIcon,
  CalendarToday,
  LocalFireDepartment,
  BarChart,
  Edit as EditIcon,
  Add as AddIcon,
  Delete as DeleteIcon,
  CheckCircle as CheckIcon,
  FilterList,
  OpenInNew as OpenInNewIcon,
  InfoOutlined as InfoOutlinedIcon,
} from "@mui/icons-material";
import dayjs from "dayjs";
import relativeTime from "dayjs/plugin/relativeTime";
import api from "services/api";
import { useTheme } from "@mui/material/styles";
import { useNavigate } from "react-router-dom";
import { useStateContext } from "contexts/ContextProvider";

dayjs.extend(relativeTime);

const MemberActivity = ({ memberId }) => {
  const theme = useTheme();
  const navigate = useNavigate();
  const { openDialog, closeDialog } = useStateContext();
  const [loading, setLoading] = useState(true);
  const [timeline, setTimeline] = useState([]);
  const [summary, setSummary] = useState(null);
  const [total, setTotal] = useState(0);
  const [languages, setLanguages] = useState([]);
  const [serviceTypes, setServiceTypes] = useState([]);
  const [linguists, setLinguists] = useState([]);
  const [referenceDataLoaded, setReferenceDataLoaded] = useState(false);
  const [filters, setFilters] = useState({
    entityType: "",
    action: "",
    limit: 20,
    offset: 0,
  });

  const mostEditedCount = summary?.mostEdited?.count ?? null;
  const mostEditedShare = summary?.mostEdited?.percentage ?? null;

  useEffect(() => {
    fetchReferenceData();
  }, []);

  const fetchReferenceData = async () => {
    try {
      const [languagesRes, serviceTypesRes, linguistsRes] = await Promise.all([
        api.get("/languages"),
        api.get("/serviceTypes"),
        api.get("/linguists/tasks"),
      ]);
      setLanguages(languagesRes.data);
      setServiceTypes(serviceTypesRes.data);
      setLinguists(linguistsRes.data);
      setReferenceDataLoaded(true);
    } catch (error) {
      console.error("Error fetching reference data:", error);
      setReferenceDataLoaded(true); // Still set to true to avoid blocking
    }
  };

  const fetchAuditData = useCallback(async () => {
    try {
      setLoading(true);
      const params = new URLSearchParams();

      Object.keys(filters).forEach((key) => {
        if (filters[key]) params.append(key, filters[key]);
      });

      const response = await api.get(
        `/auditLogs/user/${memberId}?${params.toString()}`,
      );

      const rawTimeline = Array.isArray(response?.data?.timeline)
        ? response.data.timeline
        : [];

      // Set total and summary immediately — before any merge logic that could throw.
      setSummary(response.data.summary);
      setTotal(response.data.total);

      // Merge related audit logs; fall back to raw timeline on any error.
      let timelineToRender = rawTimeline;
      try {
        const mergedTimeline = mergeRelatedAuditLogs(
          rawTimeline,
          languages,
          serviceTypes,
          linguists,
        );
        // If merge logic over-filters edge-case payloads, preserve visibility with raw logs.
        timelineToRender =
          rawTimeline.length > 0 && mergedTimeline.length === 0
            ? rawTimeline
            : mergedTimeline;
      } catch (mergeError) {
        console.error("Error merging audit logs, falling back to raw:", mergeError);
      }

      // If offset is 0, replace timeline; otherwise append
      if (filters.offset === 0) {
        setTimeline(timelineToRender);
      } else {
        setTimeline((prev) => [...prev, ...timelineToRender]);
      }
    } catch (error) {
      console.error("Error fetching audit data:", error);
    } finally {
      setLoading(false);
    }
  }, [filters, languages, linguists, memberId, serviceTypes]);

  useEffect(() => {
    if (referenceDataLoaded) {
      fetchAuditData();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [referenceDataLoaded, filters, memberId]);

  const mergeRelatedAuditLogs = (
    logs,
    languagesData = [],
    serviceTypesData = [],
    linguistsData = [],
  ) => {
    if (!logs || logs.length === 0) return logs;

    // Normalize logs: ensure NewState and PreviousState exist (use Changes as fallback for compatibility)
    const normalizeLogs = logs.map((log) => ({
      ...log,
      NewState: log.NewState || log.Changes || {},
      PreviousState: log.PreviousState || log.Changes || {},
      Changes: log.Changes || {},
    }));

    // Helper to extract parent ID from Changes or log data
    const getParentId = (log, fieldName) => {
      // First try to get from Changes object
      if (log.Changes && log.Changes[fieldName] !== undefined) {
        return log.Changes[fieldName];
      }
      // Fall back to NewState/PreviousState for backward compatibility
      if (log.NewState && log.NewState[fieldName] !== undefined) {
        return log.NewState[fieldName];
      }
      if (log.PreviousState && log.PreviousState[fieldName] !== undefined) {
        return log.PreviousState[fieldName];
      }
      return null;
    };

    // First, transform Requestor, LinguistLanguage, and LinguistAvailability logs into parent entity logs
    const transformedLogs = normalizeLogs.map((log) => {
      // Transform Requestor to Client
      if (log.EntityType === "Requestor") {
        const clientID = getParentId(log, "ClientID");
        const clientName =
          log.NewState?.Client?.Account ||
          log.PreviousState?.Client?.Account ||
          log.NewState?.Client?.ShortName ||
          log.PreviousState?.Client?.ShortName;

        // Transform Changes to have Requestor prefix
        const transformedChanges = {};
        if (log.Changes) {
          Object.entries(log.Changes).forEach(([key, value]) => {
            if (
              key !== "ClientID" &&
              key !== "id" &&
              key !== "createdAt" &&
              key !== "updatedAt"
            ) {
              transformedChanges[`Requestor.${key}`] = value;
            }
          });
        }

        // Create a Client-type log with Requestor changes
        return {
          ...log,
          EntityType: "Client",
          EntityId: clientID,
          Action: log.Action === "CREATE" ? "UPDATE" : log.Action,
          Changes: transformedChanges,
          NewState: clientName ? { Account: clientName } : {},
          PreviousState: clientName ? { Account: clientName } : {},
        };
      }

      // Keep LinguistLanguage and LinguistAvailability as-is - they'll be merged under Linguist by parentEntityMap
      return log;
    });

    const merged = [];
    const parentEntityMap = {
      ClientRate: "Client",
      ClientContractHistory: "Client",
      ClientServiceType: "Client",
      ServiceTargetLanguage: "Service",
      ServiceHistory: "Service",
      LinguistLanguage: "Linguist",
      LinguistAvailability: "Linguist",
    };

    // Helper function to get language name by ID
    const getLanguageName = (languageId) => {
      const language = languagesData.find((l) => l.id === parseInt(languageId));
      return language ? language.Name : `Language ${languageId}`;
    };

    // Helper function to get service type name by ID
    const getServiceTypeName = (serviceTypeId) => {
      const serviceType = serviceTypesData.find(
        (st) => st.id === parseInt(serviceTypeId),
      );
      return serviceType ? serviceType.Name : `ServiceType ${serviceTypeId}`;
    };

    // Helper function to get linguist name by ID
    const getLinguistName = (linguistId) => {
      const linguist = linguistsData.find((l) => l.id === parseInt(linguistId));
      return linguist ? linguist.Name : null;
    };

    // Helper function to transform field names for better display
    const transformFieldName = (entityType, fieldName) => {
      if (entityType === "ClientContractHistory") {
        const fieldMap = {
          Date: "Contract Date",
          Time: "Contract Time",
          Status: "Contract Status",
          RenewDate: "Contract Renew Date",
        };
        return fieldMap[fieldName] || fieldName;
      }
      return fieldName;
    };

    // First pass: sort logs so parents come before their children within groups of similar timestamps
    // This helps with the merging logic which looks ahead
    const timeWindow = 10000; // 10 seconds
    const sortedLogs = [...transformedLogs];

    // Group logs by parent entities first
    for (let i = 0; i < sortedLogs.length; i++) {
      const currentLog = sortedLogs[i];
      const currentTime = new Date(currentLog.Timestamp).getTime();

      // If current is a parent entity, look for children with similar timestamps
      if (!parentEntityMap[currentLog.EntityType]) {
        // Determine the parent ID field based on entity type
        let parentIdField;
        if (currentLog.EntityType === "Client") {
          parentIdField = "ClientID";
        } else if (currentLog.EntityType === "Service") {
          parentIdField = "ServiceID";
        } else if (currentLog.EntityType === "Linguist") {
          parentIdField = "LinguistID";
        }

        if (parentIdField) {
          for (let j = i + 1; j < sortedLogs.length; j++) {
            const nextLog = sortedLogs[j];
            const nextTime = new Date(nextLog.Timestamp).getTime();
            const timeDiff = Math.abs(currentTime - nextTime);

            if (timeDiff > timeWindow) break;

            // If next is a child of current, move it closer to current
            if (parentEntityMap[nextLog.EntityType] === currentLog.EntityType) {
              const childParentId = getParentId(nextLog, parentIdField);
              if (
                childParentId &&
                childParentId.toString() === currentLog.EntityId.toString()
              ) {
                // Move this child closer to its parent
                const [child] = sortedLogs.splice(j, 1);
                sortedLogs.splice(i + 1, 0, child);
              }
            }
          }
        }
      }
    }

    let i = 0;
    while (i < sortedLogs.length) {
      const currentLog = sortedLogs[i];
      const relatedLogs = [currentLog];

      // Look ahead for related logs
      let j = i + 1;
      while (j < sortedLogs.length) {
        const nextLog = sortedLogs[j];
        const timeDiff = Math.abs(
          new Date(currentLog.Timestamp) - new Date(nextLog.Timestamp),
        );

        // Check if logs should be merged together
        let shouldMerge = false;

        // Same entity type, same entity ID, and within 10 seconds
        if (
          nextLog.EntityType === currentLog.EntityType &&
          nextLog.EntityId === currentLog.EntityId &&
          nextLog.Action === currentLog.Action &&
          timeDiff < 10000
        ) {
          shouldMerge = true;
        }
        // Multiple child entities of the same parent (e.g., multiple LinguistLanguages with same LinguistID)
        else if (
          parentEntityMap[currentLog.EntityType] &&
          parentEntityMap[nextLog.EntityType] ===
            parentEntityMap[currentLog.EntityType] &&
          timeDiff < 10000
        ) {
          const parentType = parentEntityMap[currentLog.EntityType];
          const parentIdField =
            parentType === "Client"
              ? "ClientID"
              : parentType === "Service"
                ? "ServiceID"
                : "LinguistID";
          const currentParentId = getParentId(currentLog, parentIdField);
          const nextParentId = getParentId(nextLog, parentIdField);
          if (
            currentParentId &&
            currentParentId.toString() === nextParentId.toString()
          ) {
            shouldMerge = true;
          }
        }
        // Or if current is a child entity and next is its parent (e.g., ClientRate -> Client)
        // Check by comparing the child's parent ID field with parent's EntityId
        else if (
          parentEntityMap[currentLog.EntityType] === nextLog.EntityType &&
          timeDiff < 10000
        ) {
          // Get the parent ID field (e.g., ClientID for ClientRate, ServiceID for ServiceTargetLanguage)
          const parentType = parentEntityMap[currentLog.EntityType];
          const parentIdField =
            parentType === "Client"
              ? "ClientID"
              : parentType === "Service"
                ? "ServiceID"
                : "LinguistID";
          const childParentId = getParentId(currentLog, parentIdField);
          if (
            childParentId &&
            childParentId.toString() === nextLog.EntityId.toString()
          ) {
            shouldMerge = true;
          }
        }
        // Or if next is a child entity of current (e.g., Client -> ClientRate)
        else if (
          parentEntityMap[nextLog.EntityType] === currentLog.EntityType &&
          timeDiff < 10000
        ) {
          // Get the parent ID field from the child entity
          const parentType = parentEntityMap[nextLog.EntityType];
          const parentIdField =
            parentType === "Client"
              ? "ClientID"
              : parentType === "Service"
                ? "ServiceID"
                : "LinguistID";
          const childParentId = getParentId(nextLog, parentIdField);
          if (
            childParentId &&
            childParentId.toString() === currentLog.EntityId.toString()
          ) {
            shouldMerge = true;
          }
        }

        if (shouldMerge) {
          relatedLogs.push(nextLog);
          j++;
        } else {
          break;
        }
      }

      // Always process through merge logic (even for single logs) to handle transformations
      if (relatedLogs.length >= 1) {
        // Determine which log should be the primary (parent entity if available)
        const primaryLog =
          relatedLogs.find((log) => !parentEntityMap[log.EntityType]) ||
          relatedLogs[0];

        const mergedLog = {
          ...primaryLog,
          Changes: {},
          PreviousState: primaryLog.PreviousState || primaryLog.Changes || {},
          NewState: primaryLog.NewState || primaryLog.Changes || {},
        };

        // Collect all service types for aggregation
        const serviceTypesList = [];

        // Collect language-related changes separately to group them together
        const languageChanges = {};
        const languagePreviousState = {};
        const languageNewState = {};

        // Pre-process to match DELETE and CREATE operations for language entities
        // Build a map of deleted languages and created languages
        const deletedLanguages = {}; // languageName -> { rate, mode, etc }
        const createdLanguages = {}; // languageName -> { rate, mode, etc }
        const processedLogIds = new Set(); // Track which logs were matched

        relatedLogs.forEach((log) => {
          if (
            (log.EntityType === "LinguistLanguage" ||
              log.EntityType === "ClientRate") &&
            (log.Action === "DELETE" || log.Action === "CREATE")
          ) {
            const languageId = getParentId(log, "LanguageID");
            let languageName = "Unknown Language";

            // Try to get language name from snapshot data or from lookup
            if (log.NewState?.Language?.Name) {
              languageName = log.NewState.Language.Name;
            } else if (log.PreviousState?.Language?.Name) {
              languageName = log.PreviousState.Language.Name;
            } else if (languageId && languagesData.length > 0) {
              languageName = getLanguageName(languageId);
            }

            if (languageName === "ALL") {
              languageName = "Other Languages";
            }

            if (log.Action === "DELETE") {
              deletedLanguages[languageName] = {
                state: log.PreviousState || log.Changes || {},
                logId: log.id,
              };
            } else if (log.Action === "CREATE") {
              createdLanguages[languageName] = {
                state: log.NewState || log.Changes || {},
                logId: log.id,
              };
            }
          }
        });

        // Track if we matched any DELETE+CREATE pairs
        let hasMatchedPairs = false;

        // Merge deleted and created languages to show proper from/to values
        Object.keys(deletedLanguages).forEach((languageName) => {
          if (createdLanguages[languageName]) {
            // Language was deleted and recreated - show actual change
            const oldState = deletedLanguages[languageName].state;
            const newState = createdLanguages[languageName].state;

            // Mark these logs as processed so they're skipped in individual processing
            processedLogIds.add(deletedLanguages[languageName].logId);
            processedLogIds.add(createdLanguages[languageName].logId);
            hasMatchedPairs = true;

            if (oldState.Rate !== undefined && newState.Rate !== undefined) {
              languageChanges[`${languageName} Rate`] = {
                from: oldState.Rate,
                to: newState.Rate,
              };
              languagePreviousState[`${languageName} Rate`] = oldState.Rate;
              languageNewState[`${languageName} Rate`] = newState.Rate;
            }
            if (oldState.Mode !== undefined && newState.Mode !== undefined) {
              languageChanges[`${languageName} Mode`] = {
                from: oldState.Mode,
                to: newState.Mode,
              };
              languagePreviousState[`${languageName} Mode`] = oldState.Mode;
              languageNewState[`${languageName} Mode`] = newState.Mode;
            }
          }
        });

        // If we matched DELETE+CREATE pairs for language entities, change Action to UPDATE
        if (
          hasMatchedPairs &&
          (relatedLogs[0].EntityType === "LinguistLanguage" ||
            relatedLogs[0].EntityType === "ClientRate")
        ) {
          mergedLog.Action = "UPDATE";
        }

        // Merge all changes from related logs with prefixes to show source
        relatedLogs.forEach((log) => {
          // Skip logs that were already processed in pre-processing (matched DELETE+CREATE pairs)
          if (processedLogIds.has(log.id)) {
            return;
          }

          if (log.Changes) {
            // If it's a child entity, prefix the changes with entity type
            if (parentEntityMap[log.EntityType]) {
              const prefix = log.EntityType;

              // Special handling for ClientRate - group by language
              if (prefix === "ClientRate") {
                const languageId = getParentId(log, "LanguageID");
                let languageName = "Unknown Language";

                // Try to get language name from the state first
                if (log.NewState?.Language?.Name) {
                  languageName = log.NewState.Language.Name;
                } else if (log.PreviousState?.Language?.Name) {
                  languageName = log.PreviousState.Language.Name;
                } else if (languageId && languagesData.length > 0) {
                  // Lookup from fetched reference data
                  languageName = getLanguageName(languageId);
                }

                // Replace "ALL" with "Other Languages"
                if (languageName === "ALL") {
                  languageName = "Other Languages";
                }

                Object.entries(log.Changes).forEach(([key, value]) => {
                  if (key === "Rate") {
                    // Only add if not already set by pre-processing
                    if (!languageChanges[`${languageName} Rate`]) {
                      languageChanges[`${languageName} Rate`] = value;
                    }
                  } else if (key !== "LanguageID") {
                    mergedLog.Changes[
                      `${prefix}.${transformFieldName(prefix, key)}`
                    ] = value;
                  }
                });
              }
              // Special handling for ClientServiceType - collect for aggregation
              else if (prefix === "ClientServiceType") {
                // Try to get ServiceType from the log state first (if included)
                const serviceTypeId =
                  log.NewState?.ServiceTypeID ||
                  log.PreviousState?.ServiceTypeID;
                let serviceTypeName = null;

                // Check if ServiceType relationship is included in the state
                if (log.NewState?.ServiceType?.Name) {
                  serviceTypeName = log.NewState.ServiceType.Name;
                } else if (log.PreviousState?.ServiceType?.Name) {
                  serviceTypeName = log.PreviousState.ServiceType.Name;
                } else if (serviceTypeId && serviceTypesData.length > 0) {
                  // Fallback to lookup from fetched reference data
                  serviceTypeName = getServiceTypeName(serviceTypeId);
                } else if (serviceTypeId) {
                  serviceTypeName = `ServiceType ${serviceTypeId}`;
                }

                if (serviceTypeName) {
                  serviceTypesList.push(serviceTypeName);
                }
              }
              // Special handling for ClientContractHistory - use friendly field names
              else if (prefix === "ClientContractHistory") {
                Object.entries(log.Changes).forEach(([key, value]) => {
                  mergedLog.Changes[transformFieldName(prefix, key)] = value;
                });
              }
              // Special handling for LinguistLanguage - group by language
              else if (prefix === "LinguistLanguage") {
                const languageId =
                  log.NewState?.LanguageID || log.PreviousState?.LanguageID;
                let languageName = "Unknown Language";

                // Try to get language name from the state first
                if (log.NewState?.Language?.Name) {
                  languageName = log.NewState.Language.Name;
                } else if (log.PreviousState?.Language?.Name) {
                  languageName = log.PreviousState.Language.Name;
                } else if (languageId && languagesData.length > 0) {
                  // Lookup from fetched reference data
                  languageName = getLanguageName(languageId);
                }

                Object.entries(log.Changes).forEach(([key, value]) => {
                  if (key === "Rate") {
                    // Only add if not already set by pre-processing
                    const rateKey = `${languageName} Rate`;
                    if (!languageChanges[rateKey]) {
                      languageChanges[rateKey] = value;
                    }
                  } else if (key === "Mode") {
                    // Only add if not already set by pre-processing
                    const modeKey = `${languageName} Mode`;
                    if (!languageChanges[modeKey]) {
                      languageChanges[modeKey] = value;
                    }
                  } else if (key !== "LanguageID") {
                    mergedLog.Changes[`${prefix}.${key}`] = value;
                  }
                });
              }
              // Special handling for LinguistAvailability
              else if (prefix === "LinguistAvailability") {
                Object.entries(log.Changes).forEach(([key, value]) => {
                  mergedLog.Changes[`${prefix}.${key}`] = value;
                });
              } else {
                Object.entries(log.Changes).forEach(([key, value]) => {
                  mergedLog.Changes[`${prefix}.${key}`] = value;
                });
              }
            } else {
              mergedLog.Changes = { ...mergedLog.Changes, ...log.Changes };
            }
          }
          if (log.PreviousState) {
            if (parentEntityMap[log.EntityType]) {
              const prefix = log.EntityType;

              if (prefix === "ClientRate") {
                const languageId = log.PreviousState?.LanguageID;
                let languageName = "Unknown Language";

                if (log.PreviousState?.Language?.Name) {
                  languageName = log.PreviousState.Language.Name;
                } else if (languageId && languagesData.length > 0) {
                  languageName = getLanguageName(languageId);
                }

                // Replace "ALL" with "Other Languages"
                if (languageName === "ALL") {
                  languageName = "Other Languages";
                }

                Object.entries(log.PreviousState).forEach(([key, value]) => {
                  if (key === "Rate") {
                    languagePreviousState[`${languageName} Rate`] = value;
                  } else if (key !== "LanguageID") {
                    mergedLog.PreviousState[
                      `${prefix}.${transformFieldName(prefix, key)}`
                    ] = value;
                  }
                });
              } else if (prefix === "ClientContractHistory") {
                Object.entries(log.PreviousState).forEach(([key, value]) => {
                  mergedLog.PreviousState[transformFieldName(prefix, key)] =
                    value;
                });
              } else if (prefix === "LinguistLanguage") {
                const languageId = log.PreviousState?.LanguageID;
                let languageName = "Unknown Language";

                if (log.PreviousState?.Language?.Name) {
                  languageName = log.PreviousState.Language.Name;
                } else if (languageId && languagesData.length > 0) {
                  languageName = getLanguageName(languageId);
                }

                Object.entries(log.PreviousState).forEach(([key, value]) => {
                  if (key === "Rate") {
                    languagePreviousState[`${languageName} Rate`] = value;
                  } else if (key === "Mode") {
                    languagePreviousState[`${languageName} Mode`] = value;
                  } else if (key !== "LanguageID") {
                    mergedLog.PreviousState[`${prefix}.${key}`] = value;
                  }
                });
              } else if (prefix === "LinguistAvailability") {
                Object.entries(log.PreviousState).forEach(([key, value]) => {
                  mergedLog.PreviousState[`${prefix}.${key}`] = value;
                });
              } else {
                Object.entries(log.PreviousState).forEach(([key, value]) => {
                  mergedLog.PreviousState[`${prefix}.${key}`] = value;
                });
              }
            } else {
              mergedLog.PreviousState = {
                ...mergedLog.PreviousState,
                ...log.PreviousState,
              };
            }
          }
          if (log.NewState) {
            if (parentEntityMap[log.EntityType]) {
              const prefix = log.EntityType;

              if (prefix === "ClientRate") {
                const languageId = log.NewState?.LanguageID;
                let languageName = "Unknown Language";

                if (log.NewState?.Language?.Name) {
                  languageName = log.NewState.Language.Name;
                } else if (languageId && languagesData.length > 0) {
                  languageName = getLanguageName(languageId);
                }

                // Replace "ALL" with "Other Languages"
                if (languageName === "ALL") {
                  languageName = "Other Languages";
                }

                Object.entries(log.NewState).forEach(([key, value]) => {
                  if (key === "Rate") {
                    languageNewState[`${languageName} Rate`] = value;
                  } else if (key !== "LanguageID") {
                    mergedLog.NewState[
                      `${prefix}.${transformFieldName(prefix, key)}`
                    ] = value;
                  }
                });
              } else if (prefix === "ClientContractHistory") {
                Object.entries(log.NewState).forEach(([key, value]) => {
                  mergedLog.NewState[transformFieldName(prefix, key)] = value;
                });
              } else if (prefix === "LinguistLanguage") {
                const languageId = log.NewState?.LanguageID;
                let languageName = "Unknown Language";

                if (log.NewState?.Language?.Name) {
                  languageName = log.NewState.Language.Name;
                } else if (languageId && languagesData.length > 0) {
                  languageName = getLanguageName(languageId);
                }

                Object.entries(log.NewState).forEach(([key, value]) => {
                  if (key === "Rate") {
                    languageNewState[`${languageName} Rate`] = value;
                  } else if (key === "Mode") {
                    languageNewState[`${languageName} Mode`] = value;
                  } else if (key !== "LanguageID") {
                    mergedLog.NewState[`${prefix}.${key}`] = value;
                  }
                });
              } else if (prefix === "LinguistAvailability") {
                Object.entries(log.NewState).forEach(([key, value]) => {
                  mergedLog.NewState[`${prefix}.${key}`] = value;
                });
              } else {
                Object.entries(log.NewState).forEach(([key, value]) => {
                  mergedLog.NewState[`${prefix}.${key}`] = value;
                });
              }
            } else {
              mergedLog.NewState = { ...mergedLog.NewState, ...log.NewState };
            }
          }
          // Preserve ServiceType if any of the logs have it
          if (log.ServiceType && !mergedLog.ServiceType) {
            mergedLog.ServiceType = log.ServiceType;
          }
        });

        // Add all language-related changes together at the end (grouped)
        mergedLog.Changes = { ...mergedLog.Changes, ...languageChanges };
        mergedLog.PreviousState = {
          ...mergedLog.PreviousState,
          ...languagePreviousState,
        };
        mergedLog.NewState = { ...mergedLog.NewState, ...languageNewState };

        // Add aggregated Service Types if any were collected
        if (serviceTypesList.length > 0) {
          mergedLog.Changes["Service Types"] = {
            from: null,
            to: serviceTypesList.join(", "),
          };
        }

        // If all logs are child entities (no parent), transform to parent entity
        const hasParent = relatedLogs.some(
          (log) => !parentEntityMap[log.EntityType],
        );
        if (
          !hasParent &&
          relatedLogs.length > 0 &&
          parentEntityMap[relatedLogs[0].EntityType]
        ) {
          const firstLog = relatedLogs[0];
          const parentType = parentEntityMap[firstLog.EntityType];
          const parentIdField =
            parentType === "Client"
              ? "ClientID"
              : parentType === "Service"
                ? "ServiceID"
                : "LinguistID";
          const parentId =
            firstLog.NewState?.[parentIdField] ||
            firstLog.PreviousState?.[parentIdField];

          if (parentId) {
            let parentName;
            if (parentType === "Client") {
              parentName =
                firstLog.NewState?.Client?.Account ||
                firstLog.PreviousState?.Client?.Account ||
                firstLog.NewState?.Client?.ShortName ||
                firstLog.PreviousState?.Client?.ShortName;
            } else if (parentType === "Linguist") {
              // Try to get from state first, then lookup by ID
              parentName =
                firstLog.NewState?.Linguist?.Name ||
                firstLog.PreviousState?.Linguist?.Name ||
                getLinguistName(parentId);
            }

            // Transform to parent entity
            mergedLog.EntityType = parentType;
            mergedLog.EntityId = parentId;
            if (parentName) {
              if (parentType === "Client") {
                mergedLog.NewState = {
                  ...mergedLog.NewState,
                  Account: parentName,
                };
                mergedLog.PreviousState = {
                  ...mergedLog.PreviousState,
                  Account: parentName,
                };
              } else {
                mergedLog.NewState = {
                  ...mergedLog.NewState,
                  Name: parentName,
                };
                mergedLog.PreviousState = {
                  ...mergedLog.PreviousState,
                  Name: parentName,
                };
              }
            }
          }
        }

        // Filter out changes where from === to (no actual change)
        // Also filter out CREATE changes with null or false values (meaningless defaults)
        // Also filter out ServiceHistory fields (internal tracking, not user-facing)
        const filteredChanges = {};
        Object.entries(mergedLog.Changes).forEach(([key, value]) => {
          // Skip ServiceHistory fields (these are internal tracking)
          if (key.startsWith("ServiceHistory.")) {
            return;
          }

          // Check if value has from/to structure
          if (
            value &&
            typeof value === "object" &&
            "from" in value &&
            "to" in value
          ) {
            // Check if values are actually different
            let isDifferent = false;

            // Try numeric comparison first (handles "0.00" vs 0, "821.25" vs 821.25, etc.)
            const fromNum = parseFloat(value.from);
            const toNum = parseFloat(value.to);

            if (!isNaN(fromNum) && !isNaN(toNum)) {
              // Both are valid numbers - compare numerically
              isDifferent = fromNum !== toNum;
            } else {
              // Not both numbers - use regular comparison
              isDifferent = value.from !== value.to;
            }

            // Only include if values are different
            if (isDifferent) {
              // For CREATE actions, also filter out null or false "to" values
              if (
                mergedLog.Action === "CREATE" &&
                (value.to === null || value.to === false)
              ) {
                return; // Skip this field
              }
              filteredChanges[key] = value;
            }
          } else {
            // Keep non-from/to changes as is
            filteredChanges[key] = value;
          }
        });
        mergedLog.Changes = filteredChanges;

        // Only add the merged log if it has actual changes (or is a DELETE action which doesn't have Changes)
        if (
          Object.keys(filteredChanges).length > 0 ||
          mergedLog.Action === "DELETE"
        ) {
          merged.push(mergedLog);
        }
        i += relatedLogs.length;
      }
    }

    return merged;
  };

  const handleFilterChange = (field, value) => {
    setFilters((prev) => ({ ...prev, [field]: value, offset: 0 }));
  };

  const loadMore = () => {
    setFilters((prev) => ({ ...prev, offset: prev.offset + prev.limit }));
  };

  const getActionIcon = (action) => {
    switch (action) {
      case "CREATE":
        return <AddIcon sx={{ color: "white", fontSize: "14px" }} />;
      case "UPDATE":
        return <EditIcon sx={{ color: "white", fontSize: "14px" }} />;
      case "DELETE":
        return <DeleteIcon sx={{ color: "white", fontSize: "14px" }} />;
      case "CHECK":
        return <CheckIcon sx={{ color: "white", fontSize: "14px" }} />;
      default:
        return <EditIcon />;
    }
  };

  const getActionColor = (action) => {
    switch (action) {
      case "CREATE":
        return "#4caf50";
      case "UPDATE":
        return "#2196f3";
      case "DELETE":
        return "#f44336";
      case "CHECK":
        return "#9c27b0";
      default:
        return theme.palette.primary.main;
    }
  };

  const formatDateValueForDisplay = (field, value) => {
    if (value === null || value === undefined || value === "") return value;

    const normalizedField = String(field || "").toLowerCase();

    // Check time fields first (more specific)
    const isTimeField =
      normalizedField.includes("timestart") ||
      normalizedField.includes("timeend") ||
      normalizedField.includes("actualstart") ||
      normalizedField.includes("actualend");

    if (isTimeField) {
      // Try parsing as time (HH:MM:SS or HH:MM format)
      const strValue = String(value);
      const timeMatch = strValue.match(/^(\d{1,2}):(\d{2})(?::\d{2})?$/);

      if (timeMatch) {
        const hours = parseInt(timeMatch[1], 10);
        const minutes = timeMatch[2];
        const period = hours >= 12 ? "PM" : "AM";
        const displayHours = hours === 0 ? 12 : hours > 12 ? hours - 12 : hours;
        return `${displayHours}:${minutes} ${period}`;
      }

      return value;
    }

    // Check duration fields
    const isDurationField =
      normalizedField.includes("duration") ||
      (normalizedField.includes("time") &&
        !normalizedField.includes("timestamp"));

    if (isDurationField) {
      // Match HH:MM:SS or H:MM:SS format
      const strValue = String(value);
      const match = strValue.match(/^(\d{1,2}):(\d{2})(?::\d{2})?$/);
      if (match) {
        const hours = match[1];
        const minutes = match[2];
        return `${hours}:${minutes}`;
      }

      return value;
    }

    // Check date fields
    const isDateField =
      normalizedField === "date" ||
      normalizedField.endsWith("date") ||
      normalizedField.includes(" date");

    if (isDateField) {
      const parsed = dayjs(value);
      if (!parsed.isValid()) return value;
      return parsed.format("MM-DD-YYYY");
    }

    return value;
  };

  const formatChanges = (changes) => {
    if (!changes) return null;

    const displayFieldMap = {
      LinguistID: "Linguist",
      RequestorID: "Requestor",
      UnitTypeID: "Unit Type",
      CreatedBy: "Created By",
    };

    // Fields to exclude as noise - but be careful with child entities
    // For child entities, only filter out their IDs and timestamps
    const noiseFields = [
      "id",
      "createdAt",
      "updatedAt",
      "NumberOfLanguages",
      "ClientID",
      "MemberID",
      "ServiceID",
      "LanguageID",
      "ServiceTypeID",
    ];
    const childEntityNoiseFields = {
      ClientRate: ["id", "createdAt", "updatedAt", "ClientID", "LanguageID"],
      ClientContractHistory: [
        "id",
        "createdAt",
        "updatedAt",
        "ClientID",
        "MemberID",
      ],
      ClientServiceType: [
        "id",
        "createdAt",
        "updatedAt",
        "ClientID",
        "ServiceTypeID",
      ],
      ServiceTargetLanguage: ["id", "createdAt", "updatedAt", "ServiceID"],
      ServiceHistory: ["id", "createdAt", "updatedAt", "ServiceID"],
      Requestor: ["id", "createdAt", "updatedAt", "ClientID"],
      LinguistLanguage: [
        "id",
        "createdAt",
        "updatedAt",
        "LinguistID",
        "LanguageID",
      ],
      LinguistAvailability: ["id", "createdAt", "updatedAt", "LinguistID"],
    };

    const entries = Object.entries(changes).filter(([field]) => {
      // For parent entity fields, exclude noise fields
      if (noiseFields.includes(field)) return false;

      // For child entity fields like "ClientRate.id", check the entity-specific noise list
      const [entityPrefix, fieldName] = field.split(".");
      if (fieldName) {
        const entityNoiseList = childEntityNoiseFields[entityPrefix];
        if (entityNoiseList && entityNoiseList.includes(fieldName))
          return false;
      }

      return true;
    });

    if (entries.length === 0) return null;

    return entries.map(([field, change]) => {
      let from =
        change.from !== null && change.from !== undefined
          ? change.from
          : "null";
      let to =
        change.to !== null && change.to !== undefined ? change.to : "null";

      // Format arrays (like TargetLanguages) with comma-space separation
      if (Array.isArray(from)) {
        from = from.join(", ");
      }
      if (Array.isArray(to)) {
        to = to.join(", ");
      }

      from = formatDateValueForDisplay(field, from);
      to = formatDateValueForDisplay(field, to);

      // Calculate delta for numeric values
      let delta = null;
      if (typeof change.from === "number" && typeof change.to === "number") {
        const diff = change.to - change.from;
        delta = diff >= 0 ? `+${diff}` : diff.toString();
      }

      // Format field names for display - convert "Requestor.Name" to "Requestor Name"
      let displayField = displayFieldMap[field] || field;
      if (field.startsWith("Requestor.")) {
        displayField = field.replace("Requestor.", "Requestor ");
      }

      return { field: displayField, from, to, delta };
    });
  };

  const getEntityDisplayName = (log) => {
    // Use EntityName from API if available (populated by server after audit log optimization)
    if (log.EntityName) {
      // Format based on entity type
      if (log.EntityType === "Client") {
        return `Client: ${log.EntityName}`;
      } else if (log.EntityType === "Linguist") {
        return `Linguist: ${log.EntityName}`;
      } else if (log.EntityType === "Invoice") {
        return `Invoice ${log.EntityName}`;
      } else if (log.EntityType === "Service") {
        return log.EntityName; // Already formatted as "Name (ProjectNumber)"
      }
      return log.EntityName;
    }

    // Fallback to legacy metadata for backward compatibility
    if (log.Metadata?.EntityName) {
      return log.Metadata.EntityName;
    }

    // Legacy fallback: Try to extract from NewState/PreviousState (for old audit logs)
    if (log.EntityType === "Invoice") {
      const invoiceNumber =
        log.NewState?.Number || log.PreviousState?.Number || log.EntityId;
      return `Invoice ${invoiceNumber}`;
    }
    if (log.EntityType === "Service") {
      const serviceCategory =
        log.ServiceType ||
        log.Metadata?.ServiceType ||
        log.NewState?.ServiceCategory ||
        log.PreviousState?.ServiceCategory;
      const projectNumber =
        log.NewState?.ProjectNumber || log.PreviousState?.ProjectNumber;

      if (serviceCategory && projectNumber) {
        return `${serviceCategory} ${projectNumber}`;
      } else if (projectNumber) {
        return `Service ${projectNumber}`;
      } else {
        return `Service #${log.EntityId}`;
      }
    }
    if (log.EntityType === "Client") {
      const clientName =
        log.NewState?.Account ||
        log.PreviousState?.Account ||
        log.NewState?.ShortName ||
        log.PreviousState?.ShortName;
      return clientName ? `Client: ${clientName}` : `Client #${log.EntityId}`;
    }
    if (log.EntityType === "Linguist") {
      const linguistName = log.NewState?.Name || log.PreviousState?.Name;
      return linguistName
        ? `Linguist: ${linguistName}`
        : `Linguist #${log.EntityId}`;
    }
    return log.EntityType;
  };

  const handleOpenEntityProfile = (log) => {
    const { EntityType, EntityId, ServiceType } = log;
    if (!EntityType || !EntityId) return;

    let path = "";

    switch (EntityType) {
      case "Invoice":
        path = `/invoices/${EntityId}`;
        break;
      case "Client":
        path = `/clients/${EntityId}`;
        break;
      case "Linguist":
        path = `/linguists/${EntityId}`;
        break;
      case "Member":
        path = `/members/${EntityId}`;
        break;
      case "Service":
        // Use the smart lookup endpoint to determine service type
        // This is especially important after the audit log optimization
        // where ServiceType may not be available in the audit data
        api
          .get(`/services/lookup/${EntityId}`)
          .then((res) => {
            if (res.data.success && res.data.routePath) {
              navigate(res.data.routePath);
            } else {
              console.error("Invalid service lookup response:", res.data);
              openDialog({
                theme,
                title: "Cannot Open Service",
                message: "Invalid service information received.",
                primaryAction: "OK",
                primaryOnClick: closeDialog,
              });
            }
          })
          .catch((error) => {
            console.error("Error looking up service:", error);
            // Fallback: try to determine from available reference data
            const serviceInfo =
              ServiceType ||
              serviceTypes.find((st) => st.id === EntityId)?.Name;

            if (serviceInfo === "Translation") {
              navigate(`/translations/${EntityId}`);
            } else if (serviceInfo === "Interpretation") {
              navigate(`/interpretations/${EntityId}`);
            } else if (
              serviceInfo === "Other Service" ||
              serviceInfo === "Other"
            ) {
              navigate(`/otherServices/${EntityId}`);
            } else {
              openDialog({
                theme,
                title: "Cannot Open Service Profile",
                message:
                  "The service type cannot be determined. The service may have been deleted.",
                primaryAction: "OK",
                primaryOnClick: closeDialog,
              });
            }
          });
        return;
      default:
        console.error("Unknown EntityType:", EntityType);
        return;
    }

    if (path) {
      navigate(path);
    }
  };

  if (loading && timeline.length === 0) {
    return (
      <Box
        display="flex"
        justifyContent="center"
        alignItems="center"
        minHeight="400px"
      >
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box>
      {/* Summary Cards */}
      {summary && (
        <Grid container spacing={2} sx={{ mb: 3 }}>
          <Grid item xs={12} sm={6} md={3}>
            <Card elevation={2} sx={{ borderRadius: "12px", height: "100%" }}>
              <CardContent
                sx={{
                  textAlign: "center",
                  minHeight: 156,
                  display: "flex",
                  flexDirection: "column",
                  alignItems: "center",
                  justifyContent: "center",
                }}
              >
                <EditIcon
                  sx={{
                    fontSize: 40,
                    color: theme.palette.primary.main,
                    mb: 1,
                  }}
                />
                <Typography variant="h4" fontWeight="bold">
                  {summary.today}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Changes Today
                </Typography>
              </CardContent>
            </Card>
          </Grid>

          <Grid item xs={12} sm={6} md={3}>
            <Card elevation={2} sx={{ borderRadius: "12px", height: "100%" }}>
              <CardContent
                sx={{
                  textAlign: "center",
                  minHeight: 156,
                  display: "flex",
                  flexDirection: "column",
                  alignItems: "center",
                  justifyContent: "center",
                }}
              >
                <BarChart
                  sx={{
                    fontSize: 40,
                    color: theme.palette.secondary.main,
                    mb: 1,
                  }}
                />
                <Box
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    gap: 0.25,
                  }}
                >
                  <Typography variant="h4" fontWeight="bold">
                    {mostEditedCount !== null ? mostEditedCount : "N/A"}
                  </Typography>
                  <Tooltip
                    title={
                      summary.mostEdited
                        ? `${summary.mostEdited.type} accounts for ${mostEditedShare}% of this member's tracked audit log entries (${mostEditedCount} total changes).`
                        : "Shows the entity type with the highest number of tracked audit log changes for this member."
                    }
                    arrow
                  >
                    <IconButton
                      size="small"
                      sx={{ color: "text.secondary", p: 0.25 }}
                    >
                      <InfoOutlinedIcon sx={{ fontSize: 16 }} />
                    </IconButton>
                  </Tooltip>
                </Box>
                <Typography variant="body2" color="text.secondary">
                  {summary.mostEdited ? summary.mostEdited.type : "Most Edited"}
                </Typography>
                <Typography variant="caption" color="text.secondary">
                  {summary.mostEdited
                    ? `${mostEditedShare}% of tracked changes`
                    : "Most edited entity type"}
                </Typography>
              </CardContent>
            </Card>
          </Grid>

          <Grid item xs={12} sm={6} md={3}>
            <Card elevation={2} sx={{ borderRadius: "12px", height: "100%" }}>
              <CardContent
                sx={{
                  textAlign: "center",
                  minHeight: 156,
                  display: "flex",
                  flexDirection: "column",
                  alignItems: "center",
                  justifyContent: "center",
                }}
              >
                <CalendarToday sx={{ fontSize: 40, color: "#ff9800", mb: 1 }} />
                <Typography variant="h6" fontWeight="bold">
                  {summary.lastActivity
                    ? dayjs(summary.lastActivity).fromNow()
                    : "No activity"}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Last Activity
                </Typography>
              </CardContent>
            </Card>
          </Grid>

          <Grid item xs={12} sm={6} md={3}>
            <Card elevation={2} sx={{ borderRadius: "12px", height: "100%" }}>
              <CardContent
                sx={{
                  textAlign: "center",
                  minHeight: 156,
                  display: "flex",
                  flexDirection: "column",
                  alignItems: "center",
                  justifyContent: "center",
                }}
              >
                <LocalFireDepartment
                  sx={{ fontSize: 40, color: "#f44336", mb: 1 }}
                />
                <Typography variant="h4" fontWeight="bold">
                  {summary.streak}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Day Streak
                </Typography>
              </CardContent>
            </Card>
          </Grid>
        </Grid>
      )}

      {/* Filters */}
      <Paper elevation={1} sx={{ p: 2, mb: 3, borderRadius: "12px" }}>
        <Grid container spacing={2} alignItems="center">
          <Grid item xs={12} sm={6}>
            <TextField
              select
              fullWidth
              size="small"
              label="Entity Type"
              value={filters.entityType}
              onChange={(e) => handleFilterChange("entityType", e.target.value)}
            >
              <MenuItem value="">All</MenuItem>
              <MenuItem value="Service">Service</MenuItem>
              <MenuItem value="Client">Client</MenuItem>
              <MenuItem value="Linguist">Linguist</MenuItem>
              <MenuItem value="Member">Member</MenuItem>
              <MenuItem value="Invoice">Invoice</MenuItem>
            </TextField>
          </Grid>

          <Grid item xs={12} sm={6}>
            <TextField
              select
              fullWidth
              size="small"
              label="Action"
              value={filters.action}
              onChange={(e) => handleFilterChange("action", e.target.value)}
            >
              <MenuItem value="">All</MenuItem>
              <MenuItem value="CREATE">Create</MenuItem>
              <MenuItem value="UPDATE">Update</MenuItem>
              <MenuItem value="DELETE">Delete</MenuItem>
              <MenuItem value="CHECK">Check</MenuItem>
            </TextField>
          </Grid>
        </Grid>
      </Paper>

      {/* Timeline */}
      <Box>
        <Typography
          variant="h6"
          gutterBottom
          sx={{ display: "flex", alignItems: "center", gap: 1 }}
        >
          <FilterList /> Activity Timeline
          <Chip label={`${total} total`} size="small" sx={{ ml: 1 }} />
        </Typography>

        {timeline.length === 0 ? (
          <Paper
            elevation={1}
            sx={{ p: 4, textAlign: "center", borderRadius: "12px" }}
          >
            <Typography color="text.secondary">No activity found</Typography>
          </Paper>
        ) : (
          <Box sx={{ position: "relative", pl: 4 }}>
            {/* Timeline vertical line */}
            <Box
              sx={{
                position: "absolute",
                left: "16px",
                top: 0,
                bottom: 0,
                width: "2px",
                bgcolor: theme.palette.divider,
              }}
            />

            {timeline.map((log, index) => {
              const changes = formatChanges(log.Changes);
              const hasChanges = changes && changes.length > 0;

              return (
                <Box key={log.id} sx={{ position: "relative", mb: 2 }}>
                  {/* Timeline dot with icon */}
                  <Box
                    sx={{
                      position: "absolute",
                      left: "-32px",
                      top: "8px",
                      width: "28px",
                      height: "28px",
                      borderRadius: "50%",
                      bgcolor: getActionColor(log.Action),
                      border: `3px solid ${theme.palette.background.alt}`,
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      padding: "4px",
                      zIndex: 1,
                    }}
                  >
                    {getActionIcon(log.Action)}
                  </Box>

                  <Accordion
                    elevation={2}
                    sx={{
                      borderRadius: "12px !important",
                      "&:before": { display: "none" },
                    }}
                  >
                    <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                      <Box
                        sx={{
                          display: "flex",
                          alignItems: "center",
                          gap: 2,
                          width: "100%",
                        }}
                      >
                        <Box
                          sx={{
                            display: "flex",
                            alignItems: "center",
                            gap: 1,
                            flex: 1,
                          }}
                        >
                          <Box>
                            <Typography variant="body1" fontWeight="600">
                              {getEntityDisplayName(log)}
                            </Typography>
                            <Typography
                              variant="caption"
                              color="text.secondary"
                            >
                              {log.Action.charAt(0) +
                                log.Action.slice(1).toLowerCase()}{" "}
                              • {dayjs(log.Timestamp).fromNow()} •{" "}
                              {dayjs(log.Timestamp).format(
                                "MMM DD, YYYY h:mm A",
                              )}
                              {hasChanges &&
                                ` • ${changes.length} field${changes.length > 1 ? "s" : ""}`}
                            </Typography>
                          </Box>
                        </Box>
                        <Box
                          onClick={(e) => {
                            e.stopPropagation();
                            handleOpenEntityProfile(log);
                          }}
                          sx={{
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                            width: "32px",
                            height: "32px",
                            borderRadius: "6px",
                            color: theme.palette.primary.main,
                            cursor: "pointer",
                            flexShrink: 0,
                            mr: 1,
                            "&:hover": {
                              bgcolor: theme.palette.primary.main,
                              color: "white",
                            },
                          }}
                        >
                          <OpenInNewIcon sx={{ fontSize: 18 }} />
                        </Box>
                      </Box>
                    </AccordionSummary>

                    <AccordionDetails>
                      <Divider sx={{ mb: 2 }} />

                      {hasChanges ? (
                        <Box>
                          <Typography
                            variant="subtitle2"
                            gutterBottom
                            sx={{ fontWeight: "bold" }}
                          >
                            Changes:
                          </Typography>
                          {changes.map(({ field, from, to, delta }) => (
                            <Box
                              key={field}
                              sx={{
                                display: "flex",
                                alignItems: "center",
                                gap: 2,
                                p: 1,
                                borderRadius: "12px",
                                bgcolor: theme.palette.action.hover,
                                mb: 1,
                              }}
                            >
                              <Typography
                                variant="body2"
                                fontWeight="500"
                                sx={{ minWidth: "150px" }}
                              >
                                {field}:
                              </Typography>
                              <Typography
                                variant="body2"
                                color="text.secondary"
                              >
                                {String(from)}
                              </Typography>
                              <Typography variant="body2">→</Typography>
                              <Typography variant="body2" fontWeight="500">
                                {String(to)}
                              </Typography>
                              {delta && (
                                <Chip
                                  label={delta}
                                  size="small"
                                  color={
                                    delta.startsWith("+") ? "success" : "error"
                                  }
                                  sx={{ ml: "auto" }}
                                />
                              )}
                            </Box>
                          ))}
                        </Box>
                      ) : (
                        <Typography variant="body2" color="text.secondary">
                          No detailed changes available
                        </Typography>
                      )}
                    </AccordionDetails>
                  </Accordion>
                </Box>
              );
            })}
          </Box>
        )}

        {/* Load More */}
        {timeline.length < total && (
          <Box sx={{ textAlign: "center", mt: 3 }}>
            <Button variant="outlined" onClick={loadMore} disabled={loading}>
              {loading ? (
                <CircularProgress size={24} />
              ) : (
                `Load More (${timeline.length} of ${total})`
              )}
            </Button>
          </Box>
        )}
      </Box>
    </Box>
  );
};

export default MemberActivity;
